(() => {
  const $ = (selector, context = document) => context.querySelector(selector);
  const $$ = (selector, context = document) => Array.from(context.querySelectorAll(selector));

  const formatMoney = (cents) => {
    const format = window.H5Theme?.moneyFormat || '${{amount}}';
    const value = Number(cents || 0);
    const amount = (value / 100).toFixed(2);
    const noDecimals = Math.round(value / 100).toString();
    return format
      .replace(/\{\{\s*amount_no_decimals[^}]*\}\}/, noDecimals)
      .replace(/\{\{\s*amount_with_comma_separator[^}]*\}\}/, amount.replace('.', ','))
      .replace(/\{\{\s*amount[^}]*\}\}/, amount);
  };

  const toast = (message) => {
    const element = $('[data-h5-toast]');
    if (!element) return;
    element.textContent = message;
    element.classList.add('show');
    clearTimeout(element._timer);
    element._timer = setTimeout(() => element.classList.remove('show'), 1800);
  };

  const lockPage = (locked) => {
    document.documentElement.style.overflow = locked ? 'hidden' : '';
  };

  const initAnnouncement = (root = document) => {
    const bars = $$('[data-h5-announcement]', root);
    if (root === document && !bars.length) {
      document.documentElement.style.setProperty('--h5-announcement-height', '0px');
      return;
    }
    bars.forEach((bar) => {
      if (bar.dataset.h5Ready) return;
      bar.dataset.h5Ready = 'true';
      const slides = $$('.h5-announcement-slide', bar);
      let active = 0;
      let timer = null;

      const setHeight = () => {
        const globalBar = $('[data-h5-announcement]');
        document.documentElement.style.setProperty('--h5-announcement-height', `${globalBar?.offsetHeight || 0}px`);
      };
      const render = () => {
        const mobile = window.matchMedia('(max-width: 760px)').matches;
        slides.forEach((slide, index) => {
          slide.classList.toggle('is-active', !mobile || index === active);
          slide.setAttribute('aria-hidden', mobile && index !== active ? 'true' : 'false');
        });
      };
      const start = () => {
        clearInterval(timer);
        render();
        if (slides.length > 1 && window.matchMedia('(max-width: 760px)').matches) {
          const interval = Math.max(2000, Number(bar.dataset.interval || 4000));
          timer = window.setInterval(() => {
            active = (active + 1) % slides.length;
            render();
          }, interval);
        }
      };

      setHeight();
      start();
      window.addEventListener('resize', () => {
        setHeight();
        start();
      }, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(setHeight).observe(bar);
    });
  };

  const initHeader = (root = document) => {
    const header = $('[data-site-header]', root) || (root === document ? null : $('[data-site-header]'));
    if (!header || header.dataset.h5Ready) return;
    header.dataset.h5Ready = 'true';

    const update = () => header.classList.toggle('is-scrolled', window.scrollY > 70);
    update();
    window.addEventListener('scroll', update, { passive: true });

    const panel = $('[data-mobile-panel]');
    const opener = $('[data-menu-open]', header);
    const close = () => {
      panel?.classList.remove('open');
      panel?.setAttribute('aria-hidden', 'true');
      opener?.setAttribute('aria-expanded', 'false');
      lockPage(false);
    };
    const open = () => {
      panel?.classList.add('open');
      panel?.setAttribute('aria-hidden', 'false');
      opener?.setAttribute('aria-expanded', 'true');
      lockPage(true);
    };
    opener?.addEventListener('click', open);
    $('[data-menu-close]', panel)?.addEventListener('click', close);
    $$('a', panel).forEach((link) => link.addEventListener('click', close));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && panel?.classList.contains('open')) close();
    });
  };

  const initSearch = (root = document) => {
    const overlay = $('[data-search-overlay]', root) || (root === document ? null : $('[data-search-overlay]'));
    if (!overlay || overlay.dataset.h5Ready) return;
    overlay.dataset.h5Ready = 'true';
    const input = $('[data-predictive-input]', overlay);
    const results = $('[data-predictive-results]', overlay);

    const open = () => {
      overlay.classList.add('open');
      overlay.setAttribute('aria-hidden', 'false');
      lockPage(true);
      setTimeout(() => input?.focus(), 120);
    };
    const close = () => {
      overlay.classList.remove('open');
      overlay.setAttribute('aria-hidden', 'true');
      lockPage(false);
    };

    $$('[data-search-open]').forEach((button) => {
      if (button.dataset.h5Ready) return;
      button.dataset.h5Ready = 'true';
      button.addEventListener('click', open);
    });
    $('[data-search-close]', overlay)?.addEventListener('click', close);
    overlay.addEventListener('click', (event) => { if (event.target === overlay) close(); });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && overlay.classList.contains('open')) close();
    });

    let timer;
    input?.addEventListener('input', () => {
      clearTimeout(timer);
      const query = input.value.trim();
      if (query.length < 2) {
        results.innerHTML = '';
        return;
      }
      timer = setTimeout(async () => {
        try {
          const endpoint = `${window.H5Theme.routes.predictive}.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last`;
          const response = await fetch(endpoint, { headers: { Accept: 'application/json' } });
          const data = await response.json();
          const products = data?.resources?.results?.products || [];
          results.innerHTML = products.map((product) => {
            const image = product.featured_image?.url || '';
            const price = typeof product.price === 'number' ? formatMoney(product.price) : (product.price || '');
            return `<a href="${product.url}"><img src="${image}" alt=""><strong>${product.title}</strong><span>${price}</span></a>`;
          }).join('') || '<div class="h5-empty-state">No products found.</div>';
        } catch (error) {
          results.innerHTML = '';
        }
      }, 220);
    });
  };

  const getDrawer = () => $('[data-cart-drawer]');
  const getDrawerOverlay = () => $('[data-cart-overlay]');
  const cartJSON = () => fetch(`${window.H5Theme.routes.cart}.js`, { headers: { Accept: 'application/json' } }).then((response) => response.json());
  const imageAtWidth = (source, width) => {
    if (!source) return '';
    try {
      const url = new URL(source, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (error) {
      return `${source}${source.includes('?') ? '&' : '?'}width=${width}`;
    }
  };

  const updateCartCount = (cart) => {
    $$('[data-cart-count]').forEach((element) => { element.textContent = cart.item_count ? cart.item_count : ''; });
  };

  const imagePreloadCache = new Map();
  const preloadImageSource = (source, priority = 'auto') => {
    if (!source) return Promise.resolve();
    if (imagePreloadCache.has(source)) return imagePreloadCache.get(source);
    const promise = new Promise((resolve) => {
      const image = new Image();
      image.decoding = 'async';
      try { image.fetchPriority = priority; } catch (error) {}
      image.onload = async () => {
        try { await image.decode?.(); } catch (error) {}
        resolve();
      };
      image.onerror = resolve;
      image.src = source;
    });
    imagePreloadCache.set(source, promise);
    return promise;
  };

  const renderCart = async ({ preloadImages = false } = {}) => {
    const drawer = getDrawer();
    if (!drawer) return null;
    const body = $('[data-cart-items]', drawer);
    try {
      const cart = await cartJSON();
      const imageSources = cart.items.map((item) => imageAtWidth(item.featured_image?.url || '', 180)).filter(Boolean);
      if (preloadImages && imageSources.length) {
        await Promise.race([
          Promise.all(imageSources.map((source) => preloadImageSource(source, 'high'))),
          new Promise((resolve) => setTimeout(resolve, 650))
        ]);
      }
      updateCartCount(cart);
      $('[data-cart-total]', drawer).textContent = formatMoney(cart.total_price);
      body.innerHTML = cart.items.length ? cart.items.map((item) => {
        const imageSource = imageAtWidth(item.featured_image?.url || '', 180);
        return `
        <div class="h5-cart-item" data-cart-key="${item.key}">
          <a href="${item.url}"><img src="${imageSource}" width="84" height="104" loading="eager" fetchpriority="high" decoding="sync" alt=""></a>
          <div>
            <a href="${item.url}"><strong>${item.product_title}</strong></a>
            <div class="h5-cart-meta">${item.variant_title || ''}</div>
            <div class="h5-cart-qty"><button type="button" data-cart-minus>−</button><span>${item.quantity}</span><button type="button" data-cart-plus>+</button></div>
            <button class="h5-cart-remove" type="button" data-cart-remove>Remove</button>
          </div>
          <div class="h5-cart-price">${formatMoney(item.final_line_price)}</div>
        </div>`;
      }).join('') : '<div class="h5-cart-empty">Your bag is ready for an island favorite.</div>';
      $$('[data-cart-upsell]', drawer).forEach((upsell) => {
        upsell.hidden = cart.items.some((item) => String(item.product_id) === upsell.dataset.productId);
      });
      return cart;
    } catch (error) {
      body.innerHTML = '<div class="h5-cart-empty">Could not load the cart. Please refresh.</div>';
      return null;
    }
  };

  const revealCart = () => {
    const drawer = getDrawer();
    const overlay = getDrawerOverlay();
    drawer?.classList.add('open');
    overlay?.classList.add('open');
    drawer?.setAttribute('aria-hidden', 'false');
    lockPage(true);
  };

  const openCart = async ({ waitForImages = false } = {}) => {
    if (waitForImages) {
      await renderCart({ preloadImages: true });
      revealCart();
      return;
    }
    revealCart();
    renderCart();
  };
  const closeCart = () => {
    const drawer = getDrawer();
    const overlay = getDrawerOverlay();
    drawer?.classList.remove('open');
    overlay?.classList.remove('open');
    drawer?.setAttribute('aria-hidden', 'true');
    lockPage(false);
  };
  const changeCart = async (key, quantity) => {
    await fetch(`${window.H5Theme.routes.cartChange}.js`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: key, quantity })
    });
    renderCart();
  };
  const addVariant = async (variantId, quantity = 1, button = null) => {
    if (!variantId) return;
    const original = button?.textContent;
    if (button) { button.disabled = true; button.textContent = 'Adding…'; }
    try {
      const data = new FormData();
      data.append('id', variantId);
      data.append('quantity', quantity);
      const response = await fetch(`${window.H5Theme.routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: data });
      if (!response.ok) throw await response.json();
      toast('Added to your island bag');
      await openCart({ waitForImages: true });
    } catch (error) {
      toast(error.description || 'Could not add this item');
    } finally {
      if (button) { button.disabled = false; button.textContent = original; }
    }
  };

  const initGlobalCartEvents = () => {
    if (document.documentElement.dataset.h5CartReady) return;
    document.documentElement.dataset.h5CartReady = 'true';
    document.addEventListener('click', (event) => {
      const openButton = event.target.closest('[data-cart-open]');
      if (openButton) { event.preventDefault(); openCart(); return; }
      if (event.target.closest('[data-cart-close]')) { closeCart(); return; }
      if (event.target === getDrawerOverlay()) { closeCart(); return; }
      const quickAdd = event.target.closest('[data-quick-add]');
      if (quickAdd) { event.preventDefault(); addVariant(quickAdd.dataset.variantId, 1, quickAdd); return; }
      const line = event.target.closest('[data-cart-key]');
      if (!line) return;
      const quantity = Number($('.h5-cart-qty span', line)?.textContent || 1);
      if (event.target.closest('[data-cart-minus]')) changeCart(line.dataset.cartKey, Math.max(0, quantity - 1));
      if (event.target.closest('[data-cart-plus]')) changeCart(line.dataset.cartKey, quantity + 1);
      if (event.target.closest('[data-cart-remove]')) changeCart(line.dataset.cartKey, 0);
    });
  };

  const initUpsellProducts = (root = document) => {
    $$('[data-upsell-product]', root).forEach((item) => {
      if (item.dataset.h5UpsellReady) return;
      item.dataset.h5UpsellReady = 'true';
      const variantsNode = $('[data-upsell-variants]', item);
      const button = $('[data-upsell-add]', item);
      const price = $('[data-upsell-price]', item);
      const optionInputs = $$('[data-upsell-option]', item);
      let variants = [];
      try { variants = JSON.parse(variantsNode?.textContent || '[]'); } catch (error) { variants = []; }
      const defaultLabel = button?.dataset.defaultLabel || button?.textContent?.trim() || 'Add';

      const selectedOptions = () => optionInputs
        .sort((a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex))
        .map((input) => input.value);

      const update = () => {
        if (!button || !variants.length) return;
        const choices = selectedOptions();
        const variant = optionInputs.length
          ? variants.find((candidate) => candidate.options.every((option, index) => option === choices[index]))
          : variants.find((candidate) => String(candidate.id) === String(button.dataset.variantId)) || variants[0];
        if (!variant) {
          button.dataset.variantId = '';
          button.disabled = true;
          button.textContent = 'Unavailable';
          return;
        }
        button.dataset.variantId = variant.id;
        button.disabled = !variant.available;
        button.textContent = variant.available ? defaultLabel : 'Sold out';
        if (price) price.textContent = formatMoney(variant.price);
      };

      optionInputs.forEach((input) => input.addEventListener('change', update));
      button?.addEventListener('click', (event) => {
        event.preventDefault();
        if (!button.dataset.variantId || button.disabled) return;
        addVariant(button.dataset.variantId, 1, button);
      });
      update();
    });
  };

  const initReveals = (root = document) => {
    const elements = $$('[data-reveal]', root).filter((element) => !element.dataset.h5RevealReady);
    elements.forEach((element) => { element.dataset.h5RevealReady = 'true'; });
    if (!('IntersectionObserver' in window)) { elements.forEach((element) => element.classList.add('visible')); return; }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.12 });
    elements.forEach((element, index) => {
      element.style.transitionDelay = `${Math.min((index % 5) * 70, 280)}ms`;
      observer.observe(element);
    });
  };

  const initSliders = (root = document) => {
    $$('[data-product-slider]', root).forEach((slider) => {
      if (slider.dataset.h5Ready) return;
      slider.dataset.h5Ready = 'true';
      const grid = $('[data-product-track]', slider) || $('.product-grid', slider);
      const prev = $('.slider-prev', slider);
      const next = $('.slider-next', slider);
      if (!grid) return;

      const step = () => {
        const first = grid.firstElementChild;
        if (!first) return grid.clientWidth * 0.8;
        const styles = getComputedStyle(grid);
        const gap = Number.parseFloat(styles.columnGap || styles.gap || 0);
        return first.getBoundingClientRect().width + gap;
      };
      const updateArrows = () => {
        const max = Math.max(0, grid.scrollWidth - grid.clientWidth - 2);
        if (prev) prev.disabled = grid.scrollLeft <= 2;
        if (next) next.disabled = grid.scrollLeft >= max;
      };
      const move = (direction) => grid.scrollBy({ left: direction * step(), behavior: 'smooth' });
      prev?.addEventListener('click', (event) => { event.preventDefault(); move(-1); });
      next?.addEventListener('click', (event) => { event.preventDefault(); move(1); });
      grid.addEventListener('scroll', updateArrows, { passive: true });
      window.addEventListener('resize', updateArrows, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(updateArrows).observe(grid);
      requestAnimationFrame(updateArrows);
    });
  };

  const initAccordions = (root = document) => {
    $$('[data-single-accordion]', root).forEach((group) => {
      if (group.dataset.h5Ready) return;
      group.dataset.h5Ready = 'true';
      group.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-accordion-trigger]');
        if (!trigger) return;
        const item = trigger.parentElement;
        Array.from(group.children).forEach((accordion) => {
          if (accordion !== item) accordion.classList.remove('open');
        });
        item.classList.toggle('open');
      });
    });
  };

  const initProduct = (root = document) => {
    $$('[data-h5-product]', root).forEach((section) => {
      if (section.dataset.h5Ready) return;
      section.dataset.h5Ready = 'true';
      const variants = JSON.parse($('[data-product-variants]', section)?.textContent || '[]');
      const form = $('form', section);
      const idInput = $('[data-variant-id]', section);
      const price = $('[data-product-price]', section);
      const addButton = $('[data-add-to-cart]', section);
      const addLabel = $('[data-add-label]', section);
      const mainImage = $('[id^="mainProductImage"]', section);
      const mobileBar = $('[data-mobile-atc]');
      let quantity = 1;

      const selections = () => {
        const inputs = $$('[data-option-input]', section);
        const max = Math.max(0, ...inputs.map((input) => Number(input.dataset.optionIndex)));
        return Array.from({ length: max + 1 }, (_, index) => {
          const checked = $(`[data-option-input][data-option-index="${index}"]:checked`, section);
          const select = $(`select[data-option-input][data-option-index="${index}"]`, section);
          return checked?.value || select?.value;
        });
      };
      const setMainImage = async (src, zoomSrc = src) => {
        if (!mainImage || !src || mainImage.currentSrc === src || mainImage.src === src) return;
        mainImage.classList.add('is-switching');
        await preloadImageSource(src, 'high');
        mainImage.removeAttribute('srcset');
        mainImage.removeAttribute('sizes');
        mainImage.src = src;
        mainImage.dataset.zoomSrc = zoomSrc || src;
        requestAnimationFrame(() => mainImage.classList.remove('is-switching'));
      };
      const update = () => {
        const options = selections();
        const variant = variants.find((candidate) => candidate.options.every((option, index) => option === options[index]));
        $$('[data-selected-option]', section).forEach((element) => { element.textContent = options[Number(element.dataset.selectedOption)] || ''; });
        $$('.color-swatch', section).forEach((label) => label.classList.toggle('active', $('input', label)?.checked));
        $$('.size-btn', section).forEach((label) => label.classList.toggle('active', $('input', label)?.checked));
        if (!variant) {
          idInput.value = '';
          addButton.disabled = true;
          addLabel.textContent = 'Unavailable';
          return;
        }
        idInput.value = variant.id;
        price.textContent = formatMoney(variant.price);
        addButton.disabled = !variant.available;
        addLabel.textContent = variant.available ? `${addButton.dataset.baseLabel || 'Add to cart'} · ${formatMoney(variant.price)}` : 'Sold out';
        if (variant.featured_media?.preview_image?.src && mainImage) {
          const source = variant.featured_media.preview_image.src;
          const separator = source.includes('?') ? '&' : '?';
          setMainImage(`${source}${separator}width=1400`, `${source}${separator}width=2800`);
        }
        if (mobileBar) {
          $('[data-mobile-price]', mobileBar).textContent = formatMoney(variant.price);
          $('[data-mobile-option]', mobileBar).textContent = options.filter(Boolean).join(' / ');
          $('[data-mobile-add]', mobileBar).disabled = !variant.available;
        }
      };

      if (addButton && addLabel) addButton.dataset.baseLabel = addLabel.textContent.split(' · ')[0];
      $$('[data-option-input]', section).forEach((input) => input.addEventListener('change', update));
      const galleryThumbs = $$('.thumb', section);
      const gallerySources = [...new Set(galleryThumbs.map((thumb) => thumb.dataset.gallerySrc).filter(Boolean))];
      const warmGallery = () => gallerySources.forEach((source, index) => {
        preloadImageSource(source, index < 2 ? 'high' : 'low');
      });
      warmGallery();
      let galleryIndex = Math.max(0, galleryThumbs.findIndex((thumb) => thumb.classList.contains('active')));
      const activateGalleryItem = (nextIndex) => {
        if (!galleryThumbs.length) return;
        galleryIndex = (nextIndex + galleryThumbs.length) % galleryThumbs.length;
        galleryThumbs.forEach((item, index) => item.classList.toggle('active', index === galleryIndex));
        const thumb = galleryThumbs[galleryIndex];
        setMainImage(thumb.dataset.gallerySrc, thumb.dataset.galleryZoom || thumb.dataset.gallerySrc);
        thumb.scrollIntoView?.({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
      };
      galleryThumbs.forEach((thumb, index) => thumb.addEventListener('click', () => activateGalleryItem(index)));
      $('[data-gallery-prev]', section)?.addEventListener('click', (event) => { event.preventDefault(); event.stopPropagation(); activateGalleryItem(galleryIndex - 1); });
      $('[data-gallery-next]', section)?.addEventListener('click', (event) => { event.preventDefault(); event.stopPropagation(); activateGalleryItem(galleryIndex + 1); });
      $('[data-qty-minus]', section)?.addEventListener('click', () => {
        quantity = Math.max(1, quantity - 1);
        $('[data-qty-input]', section).value = quantity;
        $('[data-qty-value]', section).textContent = quantity;
      });
      $('[data-qty-plus]', section)?.addEventListener('click', () => {
        quantity = Math.min(99, quantity + 1);
        $('[data-qty-input]', section).value = quantity;
        $('[data-qty-value]', section).textContent = quantity;
      });
      form?.addEventListener('submit', (event) => { event.preventDefault(); addVariant(idInput.value, quantity, addButton); });
      $('[data-mobile-add]', mobileBar)?.addEventListener('click', (event) => addVariant(idInput.value, quantity, event.currentTarget));
      if (addButton && mobileBar && 'IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => mobileBar.classList.toggle('show', !entry.isIntersecting));
        }, { threshold: 0.15 });
        observer.observe(addButton);
      }
      update();
    });
  };

  const initProductZoom = (root = document) => {
    $$('[data-image-zoom-modal]', root).forEach((modal) => {
      if (modal.dataset.h5Ready) return;
      modal.dataset.h5Ready = 'true';
      const viewport = $('[data-zoom-viewport]', modal);
      const image = $('[data-zoom-image]', modal);
      let scale = 1;
      let x = 0;
      let y = 0;
      let dragging = false;
      let startX = 0;
      let startY = 0;
      let originX = 0;
      let originY = 0;

      const apply = () => {
        image.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
        viewport.classList.toggle('is-zoomed', scale > 1);
      };
      const reset = () => { scale = 1; x = 0; y = 0; apply(); };
      const open = (source) => {
        if (!source) return;
        image.src = source;
        reset();
        modal.classList.add('open');
        modal.setAttribute('aria-hidden', 'false');
        lockPage(true);
      };
      const close = () => {
        modal.classList.remove('open');
        modal.setAttribute('aria-hidden', 'true');
        reset();
        lockPage(false);
      };
      const section = modal.previousElementSibling?.matches('[data-h5-product]') ? modal.previousElementSibling : $('[data-h5-product]');
      $$('[data-product-zoom-open]', section || document).forEach((button) => button.addEventListener('click', () => {
        const current = $('[id^="mainProductImage"]', section || document);
        open(current?.dataset.zoomSrc || current?.currentSrc || current?.src);
      }));
      $('[data-image-zoom-close]', modal)?.addEventListener('click', close);
      $('[data-zoom-in]', modal)?.addEventListener('click', () => { scale = Math.min(4, scale + 0.5); apply(); });
      $('[data-zoom-out]', modal)?.addEventListener('click', () => { scale = Math.max(1, scale - 0.5); if (scale === 1) { x = 0; y = 0; } apply(); });
      $('[data-zoom-reset]', modal)?.addEventListener('click', reset);
      modal.addEventListener('click', (event) => { if (event.target === modal) close(); });
      viewport?.addEventListener('dblclick', () => { scale = scale > 1 ? 1 : 2.25; if (scale === 1) { x = 0; y = 0; } apply(); });
      viewport?.addEventListener('pointerdown', (event) => {
        if (scale <= 1 || event.button !== 0) return;
        dragging = true;
        startX = event.clientX;
        startY = event.clientY;
        originX = x;
        originY = y;
        viewport.setPointerCapture?.(event.pointerId);
        viewport.classList.add('is-dragging');
      });
      viewport?.addEventListener('pointermove', (event) => {
        if (!dragging) return;
        x = originX + event.clientX - startX;
        y = originY + event.clientY - startY;
        apply();
      });
      const stop = (event) => {
        if (!dragging) return;
        dragging = false;
        viewport.releasePointerCapture?.(event.pointerId);
        viewport.classList.remove('is-dragging');
      };
      viewport?.addEventListener('pointerup', stop);
      viewport?.addEventListener('pointercancel', stop);
      document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && modal.classList.contains('open')) close(); });
    });
  };

  const initSizeGuide = (root = document) => {
    const modal = $('[data-size-guide-modal]', root) || (root === document ? null : $('[data-size-guide-modal]'));
    if (!modal || modal.dataset.h5Ready) return;
    modal.dataset.h5Ready = 'true';
    const open = () => { modal.classList.add('open'); modal.setAttribute('aria-hidden', 'false'); lockPage(true); };
    const close = () => { modal.classList.remove('open'); modal.setAttribute('aria-hidden', 'true'); lockPage(false); };
    $$('[data-size-guide]').forEach((button) => button.addEventListener('click', open));
    $('[data-size-guide-close]', modal)?.addEventListener('click', close);
    modal.addEventListener('click', (event) => { if (event.target === modal) close(); });
  };

  const initFilters = (root = document) => {
    const collectionRoot = $('[data-collection-root]', root) || (root === document ? $('[data-collection-root]') : null);
    if (!collectionRoot || collectionRoot.dataset.h5Ready) return;
    collectionRoot.dataset.h5Ready = 'true';

    const sidebar = $('[data-filter-sidebar]', collectionRoot);
    const getOpenButton = () => $('[data-filter-open]', collectionRoot);
    let requestController = null;
    let priceTimer = null;

    const closeSidebar = () => {
      sidebar?.classList.remove('open');
      sidebar?.setAttribute('aria-hidden', 'true');
      getOpenButton()?.setAttribute('aria-expanded', 'false');
      lockPage(false);
    };
    const openSidebar = () => {
      sidebar?.classList.add('open');
      sidebar?.setAttribute('aria-hidden', 'false');
      getOpenButton()?.setAttribute('aria-expanded', 'true');
      lockPage(true);
    };
    $$('[data-filter-close]', collectionRoot).forEach((button) => button.addEventListener('click', closeSidebar));
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && sidebar?.classList.contains('open')) closeSidebar(); });

    const bindSingleFilters = () => {
      $$('.h5-filter', collectionRoot).forEach((detail) => {
        if (detail.dataset.h5AccordionReady) return;
        detail.dataset.h5AccordionReady = 'true';
        detail.removeAttribute('open');
        detail.addEventListener('toggle', () => {
          if (!detail.open) return;
          $$('.h5-filter', collectionRoot).forEach((other) => { if (other !== detail) other.removeAttribute('open'); });
        });
      });
    };

    const currentFilterURL = () => {
      const form = $('[data-filter-form]', collectionRoot);
      const sort = $('[data-sort-select]', collectionRoot);
      const url = new URL(form?.action || window.location.pathname, window.location.origin);
      if (form) {
        const data = new FormData(form);
        for (const [key, value] of data.entries()) {
          if (String(value).trim() !== '') url.searchParams.append(key, value);
        }
      }
      if (sort?.value) url.searchParams.set('sort_by', sort.value);
      return url;
    };

    const preloadCollectionImages = async (container) => {
      const sources = $$('img', container).slice(0, 8).map((image) => image.getAttribute('src')).filter(Boolean);
      await Promise.race([
        Promise.allSettled(sources.map((source) => new Promise((resolve) => {
          const image = new Image();
          image.decoding = 'async';
          image.onload = image.onerror = resolve;
          image.src = source;
        }))),
        new Promise((resolve) => window.setTimeout(resolve, 900))
      ]);
    };

    const replaceCollection = async (url, pushState = true) => {
      const main = $('[data-collection-main]', collectionRoot);
      main?.classList.add('is-loading');
      requestController?.abort();
      requestController = new AbortController();
      try {
        const response = await fetch(url.toString(), {
          signal: requestController.signal,
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (!response.ok) throw new Error('Collection update failed');
        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const nextMain = $('[data-collection-main]', parsed);
        const nextForm = $('[data-filter-form]', parsed);
        const nextToolbar = $('[data-collection-toolbar]', parsed);
        const currentMain = $('[data-collection-main]', collectionRoot);
        const currentForm = $('[data-filter-form]', collectionRoot);
        const currentToolbar = $('[data-collection-toolbar]', collectionRoot);
        if (nextMain) await preloadCollectionImages(nextMain);
        if (nextMain && currentMain) currentMain.replaceWith(nextMain);
        if (nextForm && currentForm) currentForm.replaceWith(nextForm);
        if (nextToolbar && currentToolbar) currentToolbar.replaceWith(nextToolbar);
        if (pushState) window.history.pushState({}, '', url.toString());
        bindSingleFilters();
        const refreshedMain = $('[data-collection-main]', collectionRoot);
        refreshedMain?.classList.add('is-entering');
        setTimeout(() => refreshedMain?.classList.remove('is-entering'), 320);
        initReveals(refreshedMain || collectionRoot);
      } catch (error) {
        if (error.name !== 'AbortError') window.location.href = url.toString();
      } finally {
        $('[data-collection-main]', collectionRoot)?.classList.remove('is-loading');
      }
    };

    bindSingleFilters();

    collectionRoot.addEventListener('change', (event) => {
      if (event.target.matches('[data-sort-select]')) {
        const url = currentFilterURL();
        replaceCollection(url);
        return;
      }
      if (!event.target.closest('[data-filter-form]')) return;
      if (event.target.matches('input[type="number"]')) {
        clearTimeout(priceTimer);
        priceTimer = setTimeout(() => replaceCollection(currentFilterURL()), 450);
      } else {
        replaceCollection(currentFilterURL());
      }
    });

    collectionRoot.addEventListener('submit', (event) => {
      if (!event.target.matches('[data-filter-form]')) return;
      event.preventDefault();
      replaceCollection(currentFilterURL());
      if (window.matchMedia('(max-width: 980px)').matches) closeSidebar();
    });

    collectionRoot.addEventListener('click', (event) => {
      if (event.target.closest('[data-filter-open]')) { event.preventDefault(); openSidebar(); return; }
      if (event.target.closest('[data-filter-close]')) { event.preventDefault(); closeSidebar(); return; }
      const filterLink = event.target.closest('.h5-active-filter, .h5-pagination a');
      if (!filterLink) return;
      event.preventDefault();
      replaceCollection(new URL(filterLink.href, window.location.origin));
    });

    window.addEventListener('popstate', () => replaceCollection(new URL(window.location.href), false));
  };



  const initProductLinks = () => {
    if (document.documentElement.dataset.h5ProductLinksReady) return;
    document.documentElement.dataset.h5ProductLinksReady = 'true';
    document.addEventListener('click', (event) => {
      const link = event.target.closest('[data-h5-view-product]');
      if (!link || !link.href) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (window.Shopify?.designMode) {
        event.preventDefault();
        event.stopImmediatePropagation();
        window.location.assign(link.href);
      }
    }, true);
  };

  const init = (root = document) => {
    initAnnouncement(root);
    initHeader(root);
    initSearch(root);
    initGlobalCartEvents();
    initProductLinks();
    initUpsellProducts(root);
    initReveals(root);
    initSliders(root);
    initAccordions(root);
    initProduct(root);
    initProductZoom(root);
    initSizeGuide(root);
    initFilters(root);
  };

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
